import { Button } from '@cabonline/ui-web';
import { CheckCircleOutlineRounded, Download } from '@mui/icons-material';
import {
  GetTripResponse,
  getAddress,
  useLocale,
} from '@multiplatform-apps/service-trips';
import { Trip } from '../../context/trips-context';
import { formatBytes } from '../../utils';
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CSVLink } from 'react-csv';

import {
  StyledCsvFileTableRow,
  StyledCsvLink,
  StyledCsvModalContentWrapper,
  StyledSuccessWrapper,
  StyledTableBody,
  StyledTableWrapper,
} from './styles';
import { CSVLinkRef, CsvDownloadProps } from './types';

export const CsvDownload: FC<CsvDownloadProps> = ({ inputData, onClose }) => {
  const { t, currentLanguage, formatDateTime } = useLocale();
  const [isDownloaded, setIsDownloaded] = useState(false);

  const transformData = (data: Trip[]) => {
    return data
      .map((trip: Trip) => {
        const numberOfTrips = trip.items.length;
        return trip.items
          .map((item: GetTripResponse) => {
            const isGroupTrip = item.coordinatedBookRefnr && numberOfTrips > 1;
            return {
              passengerId: item.passengerId,
              fullName: item.fullName,
              pickupAddress: getAddress(item.addresses, 'pickup'),
              pickUpDateTime: item.pickUpDateTime
                ? formatDateTime(new Date(item.pickUpDateTime))
                : '',
              dropOffTimeDateTime: item.dropOffTimeDateTime
                ? formatDateTime(new Date(item.dropOffTimeDateTime))
                : '',
              dropOffAddress: getAddress(item.addresses, 'destination'),
              tripStatus: t(
                `trips.status.label.${item.tripStatus.trim().toLowerCase()}`
              ),
              groupTrip: isGroupTrip
                ? t('trips.tripsList.csv.sheet.groupTrip.yesLabel')
                : t('trips.tripsList.csv.sheet.groupTrip.noLabel'),
              groupPassengers: isGroupTrip ? item.coordinatedBookRefnr : '',
            };
          })
          .flat();
      })
      .flat();
  };

  const fileHeaders = useMemo(
    () => [
      {
        label: t('trips.tripsList.csv.sheet.passengerId'),
        key: 'passengerId',
      },
      { label: t('trips.tripsList.csv.sheet.passengerName'), key: 'fullName' },
      {
        label: t('trips.tripsList.csv.sheet.pickupAddress'),
        key: 'pickupAddress',
      },
      {
        label: t('trips.tripsList.csv.sheet.pickupTime'),
        key: 'pickUpDateTime',
      },
      {
        label: t('trips.tripsList.csv.sheet.dropOffAddress'),
        key: 'dropOffAddress',
      },
      {
        label: t('trips.tripsList.csv.sheet.dropOffTime'),
        key: 'dropOffTimeDateTime',
      },
      {
        label: t('trips.tripsList.csv.sheet.status'),
        key: 'tripStatus',
      },
      {
        label: t('trips.tripsList.csv.sheet.groupTrip'),
        key: 'groupTrip',
      },
      {
        label: t('trips.tripsList.csv.sheet.groupPassengers'),
        key: 'groupPassengers',
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, currentLanguage]
  );

  const csvLink = useRef<CSVLinkRef>(null);

  const [generatedFileName, setGeneratedFileName] = useState('');
  const [generatedFileSize, setGeneratedFileSize] = useState('');

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const data = useMemo(() => transformData(inputData), [inputData]);

  useEffect(() => {
    async function populateDownloadMetadata() {
      const link = csvLink.current?.link;
      if (!link?.href) return;

      try {
        const blob = await fetch(link.href).then((r) => r.blob());
        setGeneratedFileName(link.download);
        setGeneratedFileSize(String(blob.size));
      } catch {
        // Fallback: leave default empty values for filename/size
      }
    }
    populateDownloadMetadata();
  }, []);

  const handleDownloadClick = useCallback(() => setIsDownloaded(true), []);

  return (
    <StyledCsvModalContentWrapper>
      {/* aria-live region must always be present in the DOM before content changes */}
      <div aria-live="polite" aria-atomic="true" role="status">
        {isDownloaded && (
          <StyledSuccessWrapper>
            <CheckCircleOutlineRounded fontSize="large" />
            <p>{t('trips.tripsList.csv.modal.downloadSuccess')}</p>
          </StyledSuccessWrapper>
        )}
      </div>
      {isDownloaded ? (
        <Button
          autoFocus
          label={t('trips.tripsList.csv.modal.doneLabel')}
          variant="primary"
          testID="context-modal-done-button"
          onClick={onClose}
        />
      ) : (
        <>
          <p id="download-csv-modal-text" data-testid="download-csv-modal-text">
            {t('trips.tripsList.csv.modal.text')}
          </p>
          <StyledTableWrapper>
            <StyledTableBody>
              <StyledCsvFileTableRow>
                <td>{t('trips.tripsList.csv.modal.fileName')}</td>
                <td>{generatedFileName}</td>
              </StyledCsvFileTableRow>
              <StyledCsvFileTableRow>
                <td>{t('trips.tripsList.csv.modal.fileSize')}</td>
                <td>{formatBytes(Number(generatedFileSize))}</td>
              </StyledCsvFileTableRow>
            </StyledTableBody>
          </StyledTableWrapper>
          <StyledCsvLink
            data={data}
            headers={fileHeaders}
            ref={csvLink}
            filename="trips_list.csv"
            separator=";"
            onClick={handleDownloadClick}
          >
            <Button
              label={t('trips.tripsList.csv.modal.downloadButton')}
              iconRight={<Download />}
              variant="primary"
              testID="context-modal-confirm-button"
            />
          </StyledCsvLink>
        </>
      )}
    </StyledCsvModalContentWrapper>
  );
};
